import {Injectable} from '@angular/core';
import {Register} from 'src/app/domain/command/auth.command';
import CustomerApi from 'src/app/domain/service/api/customer/customer.api';
import PlanApi from 'src/app/domain/service/api/plan.api';
import UserApi from 'src/app/domain/service/api/user.api';
import {CustomerIoService} from 'src/app/infrastructure/analytics/customer-io.service';
import {GtmService} from 'src/app/infrastructure/analytics/gtm.service';
import {environment} from 'src/environments/environment';
import UtmStore from 'src/app/infrastructure/store/utm.store';

@Injectable({
  providedIn: 'root'
})
export class TrackService {

  constructor(
    private planApi: PlanApi,
    private customerApi: CustomerApi,
    private userApi: UserApi,
    private customerIoService: CustomerIoService,
    private utmStore: UtmStore,
    private gtmService: GtmService
  ) {
  }

  public identify(userId: string, data: object) {
    this.addUtmParameters(data);

    analytics.identify(userId, data);

    this.gtmService.pushEvent({
      event: 'identify',
      userId,
      ...data
    });
  }

  public identifyCustomerIO(userId: string, data: object) {
    this.addUtmParameters(data);
    this.customerIoService.addCustomerIoScript(environment.analytics.customerIoSiteId, userId, data);
  }

  public track(eventName: string, data: object) {
    this.addUtmParameters(data);

    analytics.track(
      eventName,
      data
    );

    this.gtmService.pushEvent({
      event: this.normalizeEventName(eventName),
      ...data
    });
  }

  async trackOnboarded() {
    const customer = await this.customerApi.waitFetchAndSync();
    const user = await this.userApi.waitFetchAndSync();

    /* eslint-disable @typescript-eslint/naming-convention */
    const traits = {
      first_name: customer.name,
      surname: customer.surname,
      email: customer.email,
      encoded_email: window.btoa(customer.email),
      created_at: user.registeredAt,
      fs_contact: true,
      onboarded_at: new Date().toLocaleString(),
      client_id: this.getGAClientId()
    };

    this.addUtmParameters(traits);

    await analytics.track('Onboarded Done', traits);

    this.gtmService.pushEvent({
      event: 'onboarded_done',
      ...traits
    });
  }

  async trackLogin(username: string) {
    const data = {
      username,
      client_id: this.getGAClientId()
    };

    this.addUtmParameters(data);

    analytics.track('New Login', data);

    this.gtmService.pushEvent({
      event: 'new_login',
      ...data
    });
  }

  async trackRegister(registerCommand: Register) {
    const data = {
      first_name: registerCommand.firstName,
      surname: registerCommand.surname,
      email: registerCommand.email,
      encoded_email: window.btoa(registerCommand.email),
      invite_code: registerCommand.invitation,
      client_id: this.getGAClientId()
    };

    this.addUtmParameters(data);

    analytics.track('New Lead', data);

    this.gtmService.pushEvent({
      event: 'new_lead',
      ...data
    });
  }

  async trackCreatedContractPlan() {
    const plan = await this.planApi.waitFetchAndSync();

    const data = {
      category: 'Plan',
      action: 'Plan Created',
      label: plan.planSettings.product[0].toUpperCase() + plan.planSettings.product.slice(1),
      value: plan.planSettings.initialAmount,
      client_id: this.getGAClientId()
    };

    this.addUtmParameters(data);

    analytics.track('Plan Created', data);

    this.gtmService.pushEvent({
      event: 'plan_created',
      ...data
    });
  }

  async trackShareIntent(shareLink: string, email: string = null) {
    const data = {
      link: shareLink,
      email,
      type: email ? 'email' : 'native',
      client_id: this.getGAClientId()
    };

    this.addUtmParameters(data);

    analytics.track('Share Intent', data);

    this.gtmService.pushEvent({
      event: 'share_intent',
      ...data
    });
  }

  async trackProductSelected(product: string) {
    const data = {
      product,
      client_id: this.getGAClientId()
    };

    this.addUtmParameters(data);

    analytics.track('Product Selected', data);

    this.gtmService.pushEvent({
      event: 'product_selected',
      ...data
    });
  }

  async trackPlanConfirmed(initialAmount: number, monthlyAmount: number, product: string) {
    const dataPlanObject = {
      initialAmount,
      monthlyAmount,
      product
    };

    this.addUtmParameters(dataPlanObject);
    this.track('Plan Confirmed', dataPlanObject);

    if (monthlyAmount) {
      this.track('Plan Confirmed With Monthly', dataPlanObject);
    } else {
      this.track('Plan Confirmed Without Monthly', dataPlanObject);
    }
  }

  async trackSimulationStarted() {
    const data = {
      client_id: this.getGAClientId()
    };

    this.addUtmParameters(data);
    this.track('Simulation Started', data);
  }

  async identifyUser(userId: string, data){
    this.identify(userId,data);
    this.identifyCustomerIO(userId,data);
  }

  private getGAClientId(): string | null {
    const match = document.cookie.match(/_ga=GA1\.\d\.(\d+\.\d+)/);
    return match ? match[1] : null;
  }

  private addUtmParameters(data: any): void {
    const utmData = this.utmStore.sync();

    if (!utmData) {
      return;
    }

    if (utmData.source) {
      data.utm_source = utmData.source;
      data.source = utmData.source;
    }
    if (utmData.medium) {
      data.utm_medium = utmData.medium;
      data.medium = utmData.medium;
    }
    if (utmData.campaign) {
      data.utm_campaign = utmData.campaign;
      data.campaign = utmData.campaign;
    }
    if (utmData.content) {
      data.utm_content = utmData.content;
      data.content = utmData.content;
    }
    if (utmData.term) {
      data.utm_term = utmData.term;
      data.term = utmData.term;
    }
  }

  private normalizeEventName(eventName: string): string {
    return eventName
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }
}
